import type grapesjs from 'grapesjs';
import type { GrapesSeed } from '../templates/types.ts';
import { getSimpleModeTemplateIds } from '../templates/simpleModeConfig.ts';

export const CMS_TEMPLATE_ATTR = 'data-cms-template';

type SeedMode = 'append' | 'replace';

type ComponentOrArray = grapesjs.Component | grapesjs.Component[];

function toComponentArray(component: ComponentOrArray): grapesjs.Component[] {
  if (Array.isArray(component)) {
    return component;
  }
  return [component];
}

function forEachChild(
  cmp: grapesjs.Component,
  iterator: (child: grapesjs.Component) => void,
): void {
  const rawCollection = cmp.components?.();
  const collection = rawCollection as
    | (ReturnType<grapesjs.Component['components']> & {
        each?: (cb: (child: grapesjs.Component) => void) => void;
        models?: grapesjs.Component[];
      })
    | undefined;

  if (collection?.each) {
    collection.each((child: grapesjs.Component) => iterator(child));
    return;
  }

  const models = collection?.models;
  if (Array.isArray(models)) {
    models.forEach((child) => iterator(child));
  }
}

function markComponentsWithTemplateId(component: ComponentOrArray, templateId: string): void {
  toComponentArray(component).forEach((cmp) => {
    if (!cmp) {
      return;
    }
    const componentWithAttrs = cmp as grapesjs.Component & {
      addAttributes?: (attrs: Record<string, string>) => grapesjs.Component;
      setAttributes?: (attrs: Record<string, string>) => grapesjs.Component;
      getAttributes?: () => Record<string, string>;
    };
    if (typeof componentWithAttrs.addAttributes === 'function') {
      componentWithAttrs.addAttributes({ [CMS_TEMPLATE_ATTR]: templateId });
      return;
    }
    if (typeof componentWithAttrs.setAttributes === 'function') {
      const currentAttrs =
        typeof componentWithAttrs.getAttributes === 'function'
          ? { ...componentWithAttrs.getAttributes() }
          : {};
      componentWithAttrs.setAttributes({ ...currentAttrs, [CMS_TEMPLATE_ATTR]: templateId });
    }
  });
}

function injectSeedScripts(component: ComponentOrArray, script: string): void {
  toComponentArray(component).forEach((cmp) => {
    const addScript = (cmp as unknown as { addScript?: (code: string) => void }).addScript;
    if (typeof addScript === 'function') {
      addScript(script);
    }
  });
}

export function applySeed(
  editor: grapesjs.Editor,
  seed: GrapesSeed,
  options: { templateId: string; mode: SeedMode },
): ComponentOrArray {
  const component =
    options.mode === 'replace'
      ? editor.setComponents(seed.html, {})
      : editor.addComponents(seed.html, {});

  if (options.mode === 'replace') {
    editor.setStyle(seed.css ?? '');
  } else if (seed.css) {
    editor.addStyle(seed.css);
  }

  if (seed.js) {
    injectSeedScripts(component, seed.js);
  }

  markComponentsWithTemplateId(component, options.templateId);
  return component;
}

const CLASS_TEMPLATE_MAP: Record<string, string> = {
  'hero-split': 'home/hero-split',
  'hero-centered': 'home/hero-centered',
  'masonry-promo': 'home/masonry-promo',
  'team-focus': 'about/team-focus',
  timeline: 'about/timeline',
  'mission-stats': 'about/mission-stats',
  'blog-list-sidebar': 'blog/list-sidebar',
  'grid-cards': 'blog/grid-cards',
  'featured-plus-list': 'blog/featured-plus-list',
  'simple-form': 'contact/simple-form',
  'map-info': 'contact/map-info',
  'split-form': 'contact/split-form',
  'cms-eshop-item-card': 'eshop/item-card',
  'cms-eshop-category-showcase': 'eshop/category-showcase',
  'cms-eshop-float-buttons': 'eshop/float-buttons',
};

const DATASET_TEMPLATE_MAP: { key: string; templateId: string }[] = [
  { key: 'data-eshop-item-card', templateId: 'eshop/item-card' },
  { key: 'data-eshop-category-carousel', templateId: 'eshop/category-carousel' },
  { key: 'data-eshop-category-highlight', templateId: 'eshop/category-highlight' },
  { key: 'data-eshop-category-showcase', templateId: 'eshop/category-showcase' },
  { key: 'data-eshop-float-buttons', templateId: 'eshop/float-buttons' },
];

function normalizeClasses(cmp: grapesjs.Component): string[] {
  const classes = new Set<string>();
  const cmpClasses = (cmp.getClasses?.() as string[] | undefined) ?? [];
  cmpClasses.forEach((cls) => {
    if (cls) classes.add(cls);
  });
  const attrs = (cmp.getAttributes?.() as Record<string, unknown> | undefined) ?? {};
  const attrClass = (attrs.class ?? attrs.className) as string | undefined;
  if (typeof attrClass === 'string') {
    attrClass
      .split(/\s+/)
      .map((cls) => cls.trim())
      .filter(Boolean)
      .forEach((cls) => classes.add(cls));
  }
  return Array.from(classes);
}

export function inferTemplateId(component: grapesjs.Component): string | null {
  const tagName = (component.get('tagName') as string | undefined)?.toLowerCase();
  if (tagName !== 'section') {
    return null;
  }
  const attrs = (component.getAttributes?.() as Record<string, unknown> | undefined) ?? {};

  for (const { key, templateId } of DATASET_TEMPLATE_MAP) {
    if (attrs && Object.prototype.hasOwnProperty.call(attrs, key)) {
      return templateId;
    }
  }

  const variant = attrs?.['data-variant'];
  if (typeof variant === 'string' && variant) {
    const contactTemplate = `contact/${variant}`;
    if (getSimpleModeTemplateIds().includes(contactTemplate)) {
      return contactTemplate;
    }
  }

  const classes = normalizeClasses(component);
  for (const cls of classes) {
    const templateId = CLASS_TEMPLATE_MAP[cls];
    if (templateId) {
      return templateId;
    }
  }
  return null;
}

export function findTemplateId(component: grapesjs.Component): string | null {
  let current: grapesjs.Component | null = component;
  while (current) {
    const attrs = (current.getAttributes?.() as Record<string, unknown> | undefined) ?? {};
    const templateId = attrs?.[CMS_TEMPLATE_ATTR];
    if (typeof templateId === 'string' && templateId) {
      return templateId;
    }
    const inferred = inferTemplateId(current);
    if (inferred) {
      return inferred;
    }
    current = current.parent?.() ?? null;
  }
  return null;
}

export function ensureTemplateMetadata(editor: grapesjs.Editor): void {
  const wrapper = editor.getWrapper();
  if (!wrapper) {
    return;
  }
  const processComponent = (cmp: grapesjs.Component) => {
    const attrs = (cmp.getAttributes?.() as Record<string, unknown> | undefined) ?? {};
    if (!attrs || typeof attrs[CMS_TEMPLATE_ATTR] === 'string') {
      forEachChild(cmp, processComponent);
      return;
    }
    const inferred = inferTemplateId(cmp);
    if (inferred) {
      markComponentsWithTemplateId(cmp, inferred);
    }
    forEachChild(cmp, processComponent);
  };

  forEachChild(wrapper, processComponent);
}
